export const getSafeJson = (data) => {
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (e) {
      return { value: data };
    }
  }
  return typeof data === 'object' ? data : { value: data };
};

export const normalizeMetadata = (metadata) => {
  const parsed = getSafeJson(metadata);
  if (!parsed || typeof parsed !== 'object') return {};
  if (parsed.body) {
    return {
      ...parsed,
      body: getSafeJson(parsed.body),
    };
  }
  return parsed;
};

export const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

export const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

export const makeIconBox = (color, bg, border) => ({
  width: 36,
  height: 36,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bg,
  color,
  flexShrink: 0,
  border: `1px solid ${border}`,
});

export const ICON_INDIGO = makeIconBox(
  '#6366f1',
  'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  'rgba(99,102,241,0.18)',
);

export const ICON_RED = makeIconBox(
  '#be123c',
  'linear-gradient(135deg, rgba(190,18,60,0.12) 0%, rgba(239,68,68,0.08) 100%)',
  'rgba(190,18,60,0.18)',
);

export const ICON_CYAN = makeIconBox(
  '#0891b2',
  'linear-gradient(135deg, rgba(8,145,178,0.14) 0%, rgba(6,182,212,0.10) 100%)',
  'rgba(8,145,178,0.18)',
);

export const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

export const getLevelColor = (level) => {
  switch (level) {
    case 'ERROR': return 'error';
    case 'WARN': return 'warning';
    case 'INFO': return 'info';
    default: return 'default';
  }
};

export const summarizeQueueMetadata = (metadata) => {
  const queue = Array.isArray(metadata?.queue) ? metadata.queue : [];
  const delayMs = metadata?.delay_ms ?? metadata?.delay_config_ms ?? null;
  const remainingMs = metadata?.remaining_ms ?? metadata?.debounce_remaining_ms ?? null;
  const elapsedMs = metadata?.elapsed_ms
    ?? metadata?.processing_elapsed_ms
    ?? metadata?.elapsed_since_last_message_ms
    ?? null;
  const scheduledFor = metadata?.scheduled_for ?? null;
  const traceIds = Array.isArray(metadata?.trace_ids)
    ? metadata.trace_ids
    : queue.map((q) => q.trace_id).filter(Boolean);

  return {
    queue,
    delayMs,
    remainingMs,
    elapsedMs,
    scheduledFor,
    traceIds: Array.from(new Set(traceIds)),
  };
};

export const formatMsToSeconds = (ms) => {
  if (ms === null || ms === undefined) return null;
  return (ms / 1000).toFixed(2);
};

export const hasFromMeFlag = (metadata) => {
  if (!metadata || typeof metadata !== 'object') return false;
  const body = metadata.body || {};
  const messageArray = Array.isArray(body.messages) ? body.messages : [];
  const messageHasFlag = messageArray.some(
    (msg) => msg?.fromMe === true || msg?.key?.fromMe === true
  );
  return Boolean(
    metadata.fromMe === true ||
    body.fromMe === true ||
    body.key?.fromMe === true ||
    body.data?.fromMe === true ||
    body.data?.key?.fromMe === true ||
    messageHasFlag
  );
};

export const deriveRestauranteId = (row) => {
  const metadata = normalizeMetadata(row?.metadata);
  const body = metadata?.body || {};
  const nestedRest = metadata?.restaurante || body?.restaurante;
  return row?.restaurante_id
    ?? metadata?.restaurante_id
    ?? metadata?.restauranteId
    ?? nestedRest?.id
    ?? body?.restaurante_id
    ?? null;
};

export const deriveClienteTelefone = (row) => {
  const metadata = normalizeMetadata(row?.metadata);
  const body = metadata?.body || {};
  const nestedCliente = metadata?.cliente || body?.cliente;
  return row?.cliente_telefone
    ?? metadata?.cliente_telefone
    ?? metadata?.clienteTelefone
    ?? metadata?.phone
    ?? nestedCliente?.telefone
    ?? body?.phone
    ?? body?.cliente_telefone
    ?? null;
};

export const getStepTheme = (step = '') => {
  if (step.startsWith('WEBHOOK')) return { color: '#0d47a1', bg: '#e3f2fd' };
  if (step.startsWith('ZAPI')) return { color: '#2e7d32', bg: '#e8f5e9' };
  if (step.startsWith('GEMINI')) return { color: '#7b1fa2', bg: '#f3e5f5' };
  if (step.startsWith('AI')) return { color: '#3949ab', bg: '#e8eaf6' };
  if (step.startsWith('QUEUE')) return { color: '#ef6c00', bg: '#fff3e0' };
  if (step.startsWith('PROCESSOR')) return { color: '#00838f', bg: '#e0f7fa' };
  if (step.startsWith('LOCK')) return { color: '#455a64', bg: '#eceff1' };
  if (step.startsWith('SISCHEF')) return { color: '#ef6c00', bg: '#fff3e0' };
  return { color: '#546e7a', bg: '#eceff1' };
};

export const getElapsedFromWebhookMs = (row) => {
  const metadata = normalizeMetadata(row?.metadata);
  const raw = metadata?.elapsed_from_webhook_ms;
  if (typeof raw === 'number') return raw;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatMs = (ms) => {
  if (ms === null || ms === undefined) return '-';
  return `${ms} ms`;
};

export const formatCount = (value) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('pt-BR');
};

export const getRowClassName = (params) => {
  if (params.row.step === 'WEBHOOK_RECEIVED') {
    const metadata = normalizeMetadata(params.row.metadata);
    return hasFromMeFlag(metadata) ? 'log-row-restaurant-webhook' : 'log-row-user';
  }
  if (params.row.step.startsWith('PROCESSOR')) return 'log-row-processor';
  if (params.row.step.startsWith('QUEUE')) return 'log-row-queue';
  if (params.row.step.startsWith('LOCK')) return 'log-row-lock';
  if (params.row.step.startsWith('GEMINI')) return 'log-row-ai';
  if (params.row.step.startsWith('ZAPI')) return 'log-row-restaurant';
  if (params.row.level === 'ERROR') return 'log-row-error';
  if (params.row.level === 'WARN') return 'log-row-warn';
  return '';
};

export const LOG_STEP_OPTIONS = [
  'WEBHOOK_RECEIVED',
  'WEBHOOK_DUPLICATE',
  'VALIDATION_FAIL',
  'QUEUE_ENQUEUED',
  'GEMINI_REQUEST',
  'GEMINI_RESPONSE',
  'GEMINI_ERROR',
  'GEMINI_TIMEOUT',
  'GEMINI_RETRY',
  'ZAPI_SEND_START',
  'ZAPI_SEND_SUCCESS',
  'ZAPI_SEND_ERROR',
  'ZAPI_SEND_TIMEOUT',
  'ZAPI_SEND_RETRY',
  'ZAPI_BTN_START',
  'ZAPI_BTN_SUCCESS',
  'SISCHEF_SEND_START',
  'SISCHEF_SEND_SUCCESS',
  'SISCHEF_SEND_ERROR',
];
